// Helpers to read localized fields from outbreak rows. Each helper picks the
// right translation from the JSONB i18n column, falling back to the
// existing English text column when the requested language isn't present.
//
// Usage:
//   const { localizedName, localizedSummary } = useLocalizedOutbreak(outbreak);
//
// The hook is a thin wrapper over react-i18next's useTranslation so it
// re-renders automatically when the user switches language.

import { useTranslation } from 'react-i18next';
import { pickI18n } from '../types/supabase';
import type { OutbreakWithSources, SourceRow } from '../types/supabase';

/** Returns translated fields for an outbreak, with EN fallback. */
export function useLocalizedOutbreak(o: OutbreakWithSources | null | undefined) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  return {
    locationName: o ? pickI18n(o.location_name_i18n, o.location_name, lang) : '',
    region: o ? pickI18n(o.region_i18n, o.region, lang) : '',
    summary: o ? pickI18n(o.summary_i18n, o.summary, lang) : '',
    significance: o ? pickI18n(o.significance_i18n, o.significance, lang) : '',
  };
}

/** Returns translated source fields with EN fallback. */
export function useLocalizedSource(s: SourceRow | null | undefined) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  return {
    title: s ? pickI18n(s.title_i18n, s.title, lang) : '',
    publisher: s ? pickI18n(s.publisher_i18n, s.publisher, lang) : '',
  };
}

/**
 * Lang-aware pure helper for use outside React (e.g. in map.tsx for
 * pinned location-name labels at the GL layer).
 */
export function localizedField(
  i18n: Record<string, string> | null | undefined,
  fallback: string | null | undefined,
  lang: string,
): string {
  return pickI18n(i18n ?? null, fallback, lang);
}
